import { observable, action, computed } from 'mobx';
import type { Moment } from 'moment';
import moment from 'moment';
import type { CandidateResponseArgs } from './candidate';
import Candidate from './candidate';
import type { OpenMeetingSlotResponseArgs } from './open_meeting_slot';
import OpenMeetingSlot, {
  convertOpenMeetingSlotResponseToArgs,
  defaultOpenMeetingSlotArgs
} from './open_meeting_slot';
import type { OpenMeetingBlockedSlotResponseArgs } from './open_meeting_blocked_slot';
import OpenMeetingBlockedSlot, { convertOpenMeetingBlockedSlotResponseToArgs } from './open_meeting_blocked_slot';
import Calendar from './calendar';
import type Event from './event';

export type OpenMeetingStatus = 'hidden' | 'published';
export type OpenMeetingType = 'none' | 'google' | 'outlook' | 'zoom';

export const openMeetingStartDateOptions = [
  { label: '今すぐ', value: '0h' },
  { label: '1時間後', value: '1h' },
  { label: '2時間後', value: '2h' },
  { label: '3時間後', value: '3h' },
  { label: '4時間後', value: '4h' },
  { label: '5時間後', value: '5h' },
  { label: '明日から', value: '1d' },
  { label: '明後日から', value: '2d' },
  { label: '3日後から', value: '3d' },
  { label: '4日後から', value: '4d' },
  { label: '5日後から', value: '5d' },
  { label: '来週月曜から', value: '1wm' },
  { label: '1週間後から', value: '1w' },
  { label: '再来週月曜から', value: '2wm' },
  { label: '2週間後から', value: '2w' },
  { label: '3週間後から', value: '3w' },
  { label: '4週間後から', value: '4w' },
  { label: '来月月初から', value: '1mb' }
];

export const periodOptions = [
  { label: '1日分', value: '1d' },
  { label: '2日分', value: '2d' },
  { label: '3日分', value: '3d' },
  { label: '4日分', value: '4d' },
  { label: '5日分', value: '5d' },
  { label: '1週間分', value: '1w' },
  { label: '2週間分', value: '2w' },
  { label: '3週間分', value: '3w' },
  { label: '4週間分', value: '4w' },
  { label: '2ヶ月分', value: '2m' },
  { label: '3ヶ月分', value: '3m' },
  { label: '特定日まで', value: '' }
];

export const defaultOpenMeetingSlots = (id: number | undefined) => {
  return [0, 1, 2, 3, 4].map(day => new OpenMeetingSlot(defaultOpenMeetingSlotArgs(id, day)));
};

export type OpenMeetingResponseArgs = {
  id: number;
  key?: string;
  title: string;
  working_title: string;
  place: string;
  place_address: string;
  meeting_room: string;
  description?: string;
  start_date?: string;
  diff_to_start_date: string;
  period?: string;
  end_date?: string;
  duration: number;
  buffer_between: number;
  events_per_day: number;
  participants: {
    name?: string;
    connected_calendar_id: string;
    email: string;
    provider: string | null;
    calendar_id: string | null;
  }[];
  status?: OpenMeetingStatus;
  google_meet_url?: string;
  outlook_online_meeting_url?: string;
  zoom_meeting_join_url?: string;
  meeting_type?: OpenMeetingType;
  meeting_url?: string;
  user: {
    name: string;
    organization: {
      name: string;
    };
  };
  ref_app?: string;
  external_organization_id?: string;
  application_id?: string;
  candidates?: CandidateResponseArgs[];
  open_meeting_slots?: OpenMeetingSlotResponseArgs[];
  open_meeting_blocked_slots: OpenMeetingBlockedSlotResponseArgs[];
};

export type OpenMeetingArgs = {
  id?: number;
  key?: string;
  title: string;
  workingTitle: string;
  place: string;
  placeAddress: string;
  meetingRoom: string;
  description?: string;
  startDate?: Moment;
  diffToStartDate: string;
  period: string;
  endDate?: Moment;
  duration: number;
  bufferBetween: number;
  eventsPerDay: number;
  participants: Calendar[];
  tentativeSchedulePrefix?: string;
  status?: OpenMeetingStatus;
  googleMeetUrl?: string;
  outlookOnlineMeetingUrl?: string;
  zoomMeetingJoinUrl?: string;
  meetingType?: OpenMeetingType;
  meetingUrl?: string;
  user?: {
    name: string;
    organization: {
      name: string;
    };
  };
  refApp?: string;
  organizationId?: string;
  applicationId?: string;
  candidates: Candidate[];
  openMeetingSlots: OpenMeetingSlot[];
  openMeetingBlockedSlots: OpenMeetingBlockedSlot[];
};

export const initialOpenMeetingArgs: OpenMeetingArgs = {
  title: '',
  workingTitle: '',
  place: '',
  placeAddress: '',
  meetingRoom: '',
  duration: 60,
  bufferBetween: 15,
  diffToStartDate: '1d',
  period: '2w',
  eventsPerDay: 10,
  participants: [],
  meetingType: 'none',
  candidates: [],
  openMeetingSlots: [],
  openMeetingBlockedSlots: [],
  status: 'published'
};

export const convertOpenMeetingResponseToArgs = (response: OpenMeetingResponseArgs) => {
  const participants =
    response.participants?.map(
      p =>
        new Calendar({
          id: p.calendar_id || '',
          auth_data_id: p.connected_calendar_id,
          name: p.name || p.calendar_id || '',
          email: p.email,
          provider: p.provider
        })
    ) || [];
  const candidates =
    response.candidates?.map(
      c => new Candidate({ id: c.id, start: moment(c.start_time), end: moment(c.end_time) })
    ) || [];
  const openMeetingSlots =
    response.open_meeting_slots && response.open_meeting_slots.length > 0
      ? response.open_meeting_slots.map(s => new OpenMeetingSlot(convertOpenMeetingSlotResponseToArgs(s)))
      : defaultOpenMeetingSlots(response.id);
  const args: OpenMeetingArgs = {
    id: response.id,
    key: response.key,
    user: response.user,
    title: response.title,
    workingTitle: response.working_title,
    place: response.place,
    placeAddress: response.place_address,
    meetingRoom: response.meeting_room,
    description: response.description,
    startDate: response.start_date ? moment(response.start_date) : undefined,
    diffToStartDate: response.diff_to_start_date,
    period: response.end_date ? '' : response.period || '2w',
    endDate: response.end_date ? moment(response.end_date) : undefined,
    duration: response.duration,
    bufferBetween: response.buffer_between,
    eventsPerDay: response.events_per_day,
    status: response.status,
    refApp: response.ref_app,
    organizationId: response.external_organization_id,
    applicationId: response.application_id,
    meetingType: response.meeting_type,
    meetingUrl: response.meeting_url,
    openMeetingBlockedSlots: response.open_meeting_blocked_slots.map(
      s => new OpenMeetingBlockedSlot(convertOpenMeetingBlockedSlotResponseToArgs(s))
    ),
    participants,
    candidates,
    openMeetingSlots
  };
  return args;
};

export default class OpenMeeting {
  @observable id?: number;
  @observable key?: string;
  @observable user?: {
    name: string;
    organization: {
      name: string;
    };
  };

  @observable title: string;
  @observable workingTitle: string;
  @observable place: string;
  @observable placeAddress: string;
  @observable meetingRoom: string;
  @observable description?: string;
  @observable startDate?: Moment;
  @observable diffToStartDate: string;
  @observable period: string;
  @observable endDate?: Moment;
  @observable duration: number;
  @observable bufferBetween: number;
  @observable eventsPerDay: number;
  @observable status?: OpenMeetingStatus;
  @observable meetingType?: OpenMeetingType;
  @observable meetingUrl?: string;
  @observable participants: Calendar[];
  @observable tentativeSchedulePrefix: string;
  @observable refApp?: string;
  @observable organizationId?: string;
  @observable applicationId?: string;

  eventlists: Event[][];

  @observable candidates: Candidate[];
  @observable openMeetingSlots: OpenMeetingSlot[];
  @observable openMeetingBlockedSlots: OpenMeetingBlockedSlot[];

  constructor(args: OpenMeetingArgs) {
    this.id = args.id;
    this.key = args.key;
    this.user = args.user;
    this.title = args.title;
    this.workingTitle = args.workingTitle;
    this.place = args.place;
    this.placeAddress = args.placeAddress;
    this.meetingRoom = args.meetingRoom;
    this.description = args.description;
    this.startDate = args.startDate;
    this.diffToStartDate = args.diffToStartDate;
    this.period = args.period;
    this.endDate = args.endDate;
    this.duration = args.duration;
    this.bufferBetween = args.bufferBetween;
    this.eventsPerDay = args.eventsPerDay;
    this.status = args.status;
    this.meetingType = args.meetingType;
    this.meetingUrl = args.meetingUrl;
    this.participants = args.participants;
    this.tentativeSchedulePrefix = args.tentativeSchedulePrefix || '【調整中】';
    this.refApp = args.refApp;
    this.organizationId = args.organizationId;
    this.applicationId = args.applicationId;
    this.eventlists = [[]];
    this.candidates = args.candidates;
    this.openMeetingSlots = args.openMeetingSlots;
    this.openMeetingBlockedSlots = args.openMeetingBlockedSlots;
  }

  timeText(type: 'start' | 'end') {
    if (type === 'start') {
      return this.openMeetingSlots[0]?.startTime.format('HH:mm') || '09:00';
    }
    return this.openMeetingSlots[0]?.endTime.format('HH:mm') || '18:00';
  }

  dateText() {
    let text = '';
    if (this.startDate && this.endDate) {
      if (this.startDate.year() !== moment().year()) {
        text = this.startDate.format('YYYY年');
      }
      text += this.startDate.format('MM月DD日(ddd) -');
      text += this.endDate.format('HH:mm');
    } else {
      text = this.candidatesDateRangeText();
    }
    return text;
  }

  monthText() {
    return this.startDate?.format('YYYY年 MM月');
  }

  candidatesDateRangeText() {
    let text = '';
    if (this.startDate?.year() !== moment().year()) {
      text = this.startDate?.format('YYYY年') || '----年';
    }
    const startDateStr = this.startDate?.format('MM月DD日(ddd)');
    const endDateStr = this.endDate?.format('MM月DD日(ddd)');
    text += startDateStr === endDateStr ? endDateStr : `${startDateStr} 〜 ${endDateStr}`;
    return text;
  }

  participantNames() {
    return this.participants.map(p => p.name).join('、');
  }

  setEventlists(evts: Event[][]) {
    this.eventlists = evts;
  }

  onlineMeetingName() {
    switch (this.meetingType) {
      case 'google':
        return 'Google Meet';
      case 'outlook':
        return 'Microsoft Teams';
      case 'zoom':
        return 'Zoom';
      default:
        return '';
    }
  }

  onlineMeetingUrl() {
    switch (this.meetingType) {
      case 'google':
      case 'outlook':
      case 'zoom':
        return this.meetingUrl;
      default:
        return null;
    }
  }

  @computed
  get hidden() {
    return this.status === 'hidden';
  }

  @computed
  get published() {
    return this.status === 'published';
  }

  @computed
  get isToday() {
    return this.startDate?.isSame(moment().tz('Asia/Tokyo').format('YYYY-MM-DD'), 'day');
  }

  @computed
  get isPast() {
    return this.endDate && this.endDate < moment();
  }

  @computed
  get calcuratedStartDate() {
    const now = moment();
    const matchedNumber = this.diffToStartDate?.match(/\d/);
    const matchedUnit = this.diffToStartDate?.match(/[a-z]+/);
    const number = matchedNumber ? parseInt(matchedNumber[0], 10) : 1;
    const unit: string = matchedUnit ? matchedUnit[0] : 'd';
    if (unit === 'h' || unit === 'd' || unit === 'w') {
      return now.add(number, unit);
    }
    if (unit === 'wm') {
      // 翌週(翌々週)の月曜までの日数を出し、その分日付を進める
      return now.add(7 * number + 1 - now.day(), 'd');
    }
    // 上の分岐で漏れるのは翌月月初のみなのでそれを計算している
    return now.add(1, 'M').startOf('month');
  }

  @action
  addParticipant(p: Calendar) {
    this.participants.push(p);
  }

  @action
  removeParticipant(p: Calendar) {
    const index = this.participants.indexOf(p);
    if (index > -1) {
      this.participants.splice(index, 1);
    }
  }

  @action
  removeBlockedSlot(s: OpenMeetingBlockedSlot) {
    const index = this.openMeetingBlockedSlots.indexOf(s);
    if (index > -1) {
      this.openMeetingBlockedSlots.splice(index, 1);
    }
  }

  submitParams() {
    if (this.openMeetingSlots.length === 0) {
      this.openMeetingSlots = defaultOpenMeetingSlots(this.id);
    }

    return {
      title: this.title,
      working_title: this.workingTitle,
      place: this.place,
      place_address: this.placeAddress,
      meeting_room: this.meetingRoom,
      participants: this.participants.map(p =>
        p ? { id: p.id, provider: p.provider, auth_data_id: p.authDataId } : null
      ),
      meeting_type: this.meetingType,
      description: this.description,
      diff_to_start_date: this.diffToStartDate,
      period: this.period,
      // start_date: this.calcuratedStartDate.format(), // 作成時には不要？
      end_date: this.endDate?.format(),
      open_meeting_slots: this.openMeetingSlots.map(s => s.submitParams()),
      open_meeting_blocked_slots: this.openMeetingBlockedSlots.map(s => s.submitParams()),
      duration: this.duration,
      buffer_between: this.bufferBetween,
      events_per_day: this.eventsPerDay,
      status: this.status
    };
  }
}
